import React, { useEffect, useImperativeHandle, useMemo } from 'react';
import { Form as DynamicForm } from 'react-bootstrap';
import classNames from 'classnames';
import { createObjectFromDottedKeys } from '@helpers';
import { useForm } from '@utils';

import { FormProps } from './types';
import { Fields } from './Fields';
import { Actions } from './Actions';

const INITIAL_STATE = {};

export const Form: React.FC<FormProps> = React.memo(
    React.forwardRef(
        (
            {
                header,
                helperText,
                disabled = false,
                hideActions = false,
                apiErrorMessages,
                initialState,
                actionLabel,
                actionSchema,
                onSubmit,
                onReset,
                id,
                onChange,
                isLoading = false,
                setFields,
                fields,
                wizard,
                allowHelperTextWithField,
                formBodyClassName,
                center,
                handlersRef,
                ...restProps
            },
            ref,
        ) => {
            const formId = useMemo(() => id || 'geeks-dynamic-form', [id]),
                // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                { values, formKey, handlers, errorMessages, addErrorMessage, setErrorMessages } = useForm(initialState ?? INITIAL_STATE),
                fieldSchema = useMemo(
                    () => (typeof fields === 'object' ? fields.reduce((acc, cur) => ({ ...acc, [cur.name]: cur }), {}) : fields(values)),
                    [values, fields],
                ),
                formFields = useMemo(() => (typeof fieldSchema === 'object' ? fieldSchema : fieldSchema(values)), [values, fieldSchema]);

            useEffect(() => {
                apiErrorMessages && Object.keys(apiErrorMessages).length && setErrorMessages(apiErrorMessages);
            }, [apiErrorMessages, setErrorMessages]);

            useEffect(() => {
                onChange && onChange(createObjectFromDottedKeys(values));
            }, [values, onChange]);

            const headerText =
                header !== null && header !== undefined ? (
                    <div className="module-header align-items-center justify-content-center">
                        <h2 className="m-0 p-0 col position-relative">{header}</h2>
                        {helperText && <div className="form-text">{helperText}</div>}
                    </div>
                ) : (
                    <></>
                );

            useImperativeHandle(handlersRef, () => ({
                handleChange(name, cb) {
                    const currentValue = values[name];

                    handlers.handleChange(name, cb(currentValue));
                },
            }));

            return (
                <div className="form-module module">
                    <DynamicForm
                        {...restProps}
                        id={formId}
                        ref={ref as any}
                        key={formKey}
                        onSubmit={handlers.handleFormSubmit(onSubmit)}
                        onReset={handlers.handleFormReset(onReset)}
                        className="input-form"
                        noValidate
                    >
                        {headerText}
                        <div className={classNames('form-body', formBodyClassName)}>
                            <Fields
                                errorMessages={errorMessages}
                                addErrorMessage={addErrorMessage}
                                fields={formFields}
                                values={values}
                                handlers={handlers}
                                disabled={disabled || isLoading}
                                setFields={setFields}
                                mainFields={fields}
                                allowHelperTextWithField={allowHelperTextWithField}
                                center={center}
                            />
                        </div>
                        {!hideActions && !wizard && (
                            <Actions
                                actionSchema={actionSchema}
                                formId={formId}
                                isLoading={isLoading}
                                disabled={disabled}
                                actionLabel={actionLabel}
                            />
                        )}
                    </DynamicForm>
                </div>
            );
        },
    ),
);
Form.displayName = 'Form';
